import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import { useParams } from 'react-router-dom';
import { Controller, useFieldArray } from 'react-hook-form';
import { Text, Stack, HStack, FormLabel, Grid } from '@chakra-ui/react';
import { CustomTextarea, InputText } from '../../../../components/Input';
import CoverInput from '../CoverInput';
import { fetchData } from '../../../../services/fetch';
import TemplateSelectEdit from './TemplateSelectEdit';
import QuestionCardCustom from '../../../../components/Card/Question-card-custom';

const baseUrl = process.env.REACT_APP_URL_CONSULTING_RESEARCH_API;

const PillarFormEdit = React.memo(
 ({
  index,
  field,
  templates,
  templatesIsLoading,
  register,
  control,
  setValue,
  trigger,
  errors,
  setImageUpdated,
  themeRef,
 }) => {
  const { id: surveyId } = useParams();
  const numberOfThemes = parseInt(field?.numberOfThemes) || 0;
  const numberOfLevels = parseInt(field?.numberOfLevels) || 0;
  const pillarImage = field?.pillarImage || null;

  const [initialLevelsVerified, setInitialLevelsVerified] = useState(false);
  const [imageURL, setImageURL] = useState('');
  const [isImageReal, setIsImageReal] = useState(false);
  const [selectedQuestionsByLevel, setSelectedQuestionsByLevel] = useState(
   Array.from({ length: parseInt(field?.numberOfLevels) || 0 }, () => [])
  );

  const { data: questionsTheme } = useSWR(
   themeRef && field?.pillarId
    ? `/survey/diagnostic/${surveyId}/pillar?id_theme=${themeRef}&id_pillar=${field?.pillarId}`
    : null,
   fetchData
  );

  const encodedImgPillar =
   pillarImage && pillarImage?.startsWith('pillar/')
    ? pillarImage?.replace('pillar/', 'pillar%2F')
    : null;

  const {
   fields: themes,
   append: appendThemes,
   remove: removeThemes,
  } = useFieldArray({
   control,
   name: `form.${index}.themes`,
  });

  const {
   fields: levels,
   append: appendLevels,
   remove: removeLevels,
   update: updateLevels,
  } = useFieldArray({
   control,
   name: `form.${index}.levels`,
  });

  useEffect(() => {
   const fetchImageBlob = async () => {
    const response = await fetch(baseUrl + `/image/${encodedImgPillar}`);
    setImageURL(response?.url);
    setIsImageReal(true);
   };

   if (encodedImgPillar && !isImageReal) {
    fetchImageBlob();
   }
  }, [encodedImgPillar, isImageReal]);

  useEffect(() => {
   if (themes?.length !== numberOfThemes && numberOfThemes > 0) {
    if (numberOfThemes > themes?.length) {
     const themesToAdd = numberOfThemes - themes.length;

     for (let i = 0; i < themesToAdd; i++) {
      appendThemes({
       title: '',
      });
     }
    }
    if (numberOfThemes < themes?.length) {
     const itemsToRemove = themes?.length - numberOfThemes;
     for (let i = 0; i < itemsToRemove; i++) {
      const lastIndex = themes?.length - 1;
      removeThemes(lastIndex);
     }
    }
   }
  }, [themes, numberOfThemes, removeThemes]);

  useEffect(() => {
   if (
    levels?.length &&
    questionsTheme?.questions_theme?.length &&
    !initialLevelsVerified
   ) {
    const { questions_theme } = questionsTheme;
    const transformedData = questions_theme?.map((item) =>
     item?.questions?.map((question) => question)
    );
    setSelectedQuestionsByLevel(transformedData);
    setInitialLevelsVerified(true);
   }
  }, [levels, questionsTheme, initialLevelsVerified]);

  useEffect(() => {
   if (levels?.length !== numberOfLevels && numberOfLevels > 0) {
    if (levels?.length === 0) {
     let newSelectedQuestions = [];
     for (let i = 0; i < numberOfLevels; i++) {
      appendLevels({
       nivel: 1 + i,
       questions: [],
      });
      newSelectedQuestions?.push([]);
     }
     setSelectedQuestionsByLevel(newSelectedQuestions);
     setInitialLevelsVerified(true);
    } else if (numberOfLevels > levels?.length) {
     const levelsToAdd = numberOfLevels - levels.length;

     const newSelectedQuestions = [...selectedQuestionsByLevel];

     for (let i = 0; i < levelsToAdd; i++) {
      appendLevels({
       nivel: levels.length + 1 + i,
       questions: [],
      });
      newSelectedQuestions.push([]);
     }

     setSelectedQuestionsByLevel(newSelectedQuestions);
    }
   }
  }, [levels, numberOfLevels, field]);

  useEffect(() => {
   if (numberOfLevels < levels.length) {
    const itemsToRemove = levels?.length - numberOfLevels;
    const newSelectedQuestions = [...selectedQuestionsByLevel];

    for (let i = 0; i < itemsToRemove; i++) {
     const lastIndex = levels.length - 1 - i;
     removeLevels(lastIndex);
     newSelectedQuestions.pop();
    }
    setSelectedQuestionsByLevel(newSelectedQuestions);
   }
  }, [numberOfLevels, levels, removeLevels, selectedQuestionsByLevel, field]);

  const removeQuestion = (level, questionId) => {
   const levelIndex = level - 1;
   if (levelIndex >= 0 && levelIndex < selectedQuestionsByLevel.length) {
    const updatedQuestions = selectedQuestionsByLevel[levelIndex]?.filter(
     (question) => question?.id_question_template !== questionId
    );

    const newSelectedQuestionsByLevel = selectedQuestionsByLevel?.map((questions, idx) =>
     idx === levelIndex ? updatedQuestions : questions
    );
    setSelectedQuestionsByLevel(newSelectedQuestionsByLevel);

    if (levelIndex >= 0 && levelIndex < levels?.length) {
     const updatedQuestions = [...levels[levelIndex]?.questions];
     const questionIndex = updatedQuestions?.indexOf(questionId);
     if (questionIndex !== -1) {
      updatedQuestions?.splice(questionIndex, 1);
      updateLevels(levelIndex, {
       nivel: levelIndex + 1,
       questions: updatedQuestions,
      });
     }
    }
   }
  };

  return (
   <Stack p='6' spacing='25px'>
    <HStack spacing='6'>
     <CoverInput
      pillarIndex={index}
      setValue={setValue}
      trigger={trigger}
      setImageUpdated={setImageUpdated}
      imageUrl={imageURL}
     />
     <CustomTextarea
      {...register(`form.${index}.pillarText`)}
      error={errors?.form?.[index]?.pillarText}
      label='Texto do pilar'
      placeholder='Digite o texto do pilar'
      h='151px'
     />
    </HStack>
    <HStack spacing='6'>
     <Stack w='432px'>
      <Controller
       control={control}
       rules={{
        required: true,
       }}
       render={({ field: { onChange, value } }) => (
        <InputText
         label='Título de ajuda/helper'
         placeholder='Digite o título de ajuda/helper'
         labelSize='sm'
         onChange={onChange}
         value={value}
        />
       )}
       name={`form.${index}.helpTitle`}
      />
     </Stack>

     <InputText
      {...register(`form.${index}.helpText`)}
      error={errors?.form?.[index]?.helpText}
      label='Texto de ajuda/helper'
      placeholder='Digite o texto de ajuda/helper'
      labelSize='sm'
     />
    </HStack>

    <Stack spacing='4'>
     <Text fontWeight={600}>Temas</Text>
     <Grid templateColumns='repeat(2, 1fr)' gap='48px' rowGap='4'>
      {themes?.map((theme, themeIndex) => (
       <HStack key={theme?.id} spacing='6'>
        <FormLabel
         htmlFor={`form.${index}.themes.${themeIndex}.title`}
         color='#5C6066'
         fontSize='sm'
         fontWeight={600}
         whiteSpace='nowrap'
         m={0}
        >
         Tema {themeIndex + 1}
        </FormLabel>
        <InputText
         {...register(`form.${index}.themes.${themeIndex}.title`)}
         error={errors?.form?.[index]?.themes?.[themeIndex]?.themeName}
         placeholder='Digite o nome do tema'
         labelSize='sm'
        />
       </HStack>
      ))}
     </Grid>
    </Stack>

    {Array.from(
     { length: parseInt(field?.numberOfLevels) || 0 },
     (_, index) => index + 1
    )?.map((level, i) => (
     <Stack key={level} spacing={6}>
      <HStack h='51px' bg='#EAEEF0' p='16px'>
       <Text color='#5C6066' fontWeight={600}>
        Perguntas - Nível {level}
       </Text>
      </HStack>
      <TemplateSelectEdit
       level={level}
       templates={templates}
       templatesIsLoading={templatesIsLoading}
       selectedQuestions={selectedQuestionsByLevel[i] || []}
       setSelectedQuestions={setSelectedQuestionsByLevel}
       updateLevels={updateLevels}
       initialLevelsVerified={initialLevelsVerified}
       levels={levels}
      />

      {selectedQuestionsByLevel[i]?.map((question, i) => (
       <QuestionCardCustom
        key={question?.id_question_template}
        index={i}
        remove={() => removeQuestion(level, question?.id_question_template)}
        question={question}
       />
      ))}
     </Stack>
    ))}
   </Stack>
  );
 }
);

export default PillarFormEdit;
